import * as Y from 'yjs';
import { Logger } from '@/utils/logger';

const logger = Logger.getLoggerWithPrefix('LocalDocBackupStore');

const DB_NAME = 'creately-doc-backups';
const DB_VERSION = 1;
const STORE = 'backups';
const MAX_BACKUPS_PER_DIAGRAM = 5;
const EMERGENCY_KEY_PREFIX = 'creately-emergency-backup:';

export interface LocalBackupRecord {
  id: string;                    // `${diagramId}:${timestamp}`
  diagramId: string;
  snapshotBytes: ArrayBuffer;
  createdAt: number;
  trigger: 'periodic' | 'visibility_hidden' | 'before_unload' | 'server_confirmed' | 'manual' | 'pre_restore';
  sizeBytes: number;
  serverSynced: boolean;
  latestSeq: number | null;
  docStats: { shapesCount: number; connectorsCount: number };
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('byDiagram', 'diagramId', { unique: false });
        store.createIndex('byDiagramTime', ['diagramId', 'createdAt'], { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function getDocStats(doc: Y.Doc): { shapesCount: number; connectorsCount: number } {
  try {
    return {
      shapesCount: doc.getMap('shapes').size,
      connectorsCount: doc.getMap('connectors').size,
    };
  } catch {
    return { shapesCount: 0, connectorsCount: 0 };
  }
}

export const LocalDocBackupStore = {
  async save(
    diagramId: string,
    doc: Y.Doc,
    trigger: LocalBackupRecord['trigger'],
    latestSeq: number | null,
  ): Promise<void> {
    try {
      const snapshot = Y.encodeStateAsUpdate(doc);
      const now = Date.now();
      const record: LocalBackupRecord = {
        id: `${diagramId}:${now}`,
        diagramId,
        snapshotBytes: snapshot.buffer.slice(
          snapshot.byteOffset,
          snapshot.byteOffset + snapshot.byteLength,
        ),
        createdAt: now,
        trigger,
        sizeBytes: snapshot.byteLength,
        serverSynced: trigger === 'server_confirmed',
        latestSeq,
        docStats: getDocStats(doc),
      };

      const db = await openDB();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite');
        const store = tx.objectStore(STORE);
        store.put(record);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });

      await this.prune(diagramId);
      logger.info('Backup saved', { trigger, sizeBytes: record.sizeBytes, diagramId });
    } catch (err) {
      logger.error('Failed to save backup', err);
    }
  },

  async markSynced(diagramId: string, seq: number): Promise<void> {
    try {
      const backups = await this.list(diagramId);
      if (backups.length === 0) return;
      const latest = backups[0];
      if (latest.serverSynced) return;

      const db = await openDB();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite');
        const store = tx.objectStore(STORE);
        const updated = { ...latest, serverSynced: true, latestSeq: seq };
        store.put(updated);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } catch (err) {
      logger.error('Failed to mark backup synced', err);
    }
  },

  async list(diagramId: string): Promise<LocalBackupRecord[]> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const store = tx.objectStore(STORE);
      const index = store.index('byDiagram');
      const req = index.getAll(diagramId);
      req.onsuccess = () => {
        const results = (req.result as LocalBackupRecord[])
          .sort((a, b) => b.createdAt - a.createdAt);
        resolve(results);
      };
      req.onerror = () => reject(req.error);
    });
  },

  async load(id: string): Promise<LocalBackupRecord | undefined> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const store = tx.objectStore(STORE);
      const req = store.get(id);
      req.onsuccess = () => resolve(req.result as LocalBackupRecord | undefined);
      req.onerror = () => reject(req.error);
    });
  },

  async delete(id: string): Promise<void> {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  },

  async deleteAll(diagramId: string): Promise<void> {
    const backups = await this.list(diagramId);
    if (backups.length === 0) return;
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      for (const b of backups) store.delete(b.id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  },

  async prune(diagramId: string): Promise<void> {
    const backups = await this.list(diagramId); // newest first
    if (backups.length <= MAX_BACKUPS_PER_DIAGRAM) return;

    const toDelete = backups.slice(MAX_BACKUPS_PER_DIAGRAM);
    // Never delete the most recent server-synced record
    const latestSynced = backups.find((b) => b.serverSynced);
    const deleteIds = toDelete
      .filter((b) => !latestSynced || b.id !== latestSynced.id)
      .map((b) => b.id);

    if (deleteIds.length === 0) return;
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      for (const id of deleteIds) store.delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  },

  saveEmergency(diagramId: string, doc: Y.Doc): void {
    try {
      const snapshot = Y.encodeStateAsUpdate(doc);
      // Convert to base64 for localStorage (sync API)
      let binary = '';
      const bytes = new Uint8Array(snapshot);
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64 = btoa(binary);

      // Guard against very large docs (~5MB localStorage limit shared)
      if (base64.length > 4_000_000) {
        logger.warn('Emergency backup too large, skipping', { size: base64.length });
        return;
      }

      const key = `${EMERGENCY_KEY_PREFIX}${diagramId}`;
      localStorage.setItem(key, JSON.stringify({
        data: base64,
        timestamp: Date.now(),
        shapesCount: doc.getMap('shapes').size,
      }));
      logger.info('Emergency backup saved to localStorage', { diagramId, size: base64.length });
    } catch (err) {
      logger.error('Failed to save emergency backup', err);
    }
  },

  recoverEmergency(diagramId: string): Uint8Array | null {
    try {
      const key = `${EMERGENCY_KEY_PREFIX}${diagramId}`;
      const raw = localStorage.getItem(key);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      const binary = atob(parsed.data);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }

      // Clean up after recovery
      localStorage.removeItem(key);
      logger.info('Emergency backup recovered', { diagramId, size: bytes.byteLength, savedAt: parsed.timestamp });
      return bytes;
    } catch (err) {
      logger.error('Failed to recover emergency backup', err);
      // Clean up corrupted data
      try { localStorage.removeItem(`${EMERGENCY_KEY_PREFIX}${diagramId}`); } catch {}
      return null;
    }
  },
};
